// src/features/treasury/hooks/useTreasury.js
// ✅ مع Pagination — يجلب صفحة بصفحة بدلاً من كل البيانات دفعة واحدة
import { useState, useCallback, useEffect } from 'react';
import {
  fetchTreasury, fetchTreasuryTransactions
} from '../../../api';

const PAGE_SIZE = 50;

export const useTreasury = () => {
  const [treasury, setTreasury] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ start_date: '', end_date: '', transaction_type: 'all', search: '' });

  // ── Pagination state ──
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const loadTreasury = useCallback(async () => {
      try {
          const res = await fetchTreasury();
          // API lists return array, but here it's customized to return object or array of length 1
          setTreasury(Array.isArray(res.data) ? res.data[0] : res.data);
      } catch (err) {
          console.error('Failed to load treasury', err);
      }
  },[]);

  const loadTransactions = useCallback(async (page = 1) => {
      setLoading(true);
      setError('');
      try {
          const res = await fetchTreasuryTransactions({ ...filters, page, page_size: PAGE_SIZE });
          const data = res.data;

          if (data.results !== undefined) {
              setTransactions(data.results ?? []);
              setTotalPages(data.total_pages ?? 1);
              setTotalCount(data.count ?? data.results?.length ?? 0);
              setCurrentPage(data.current_page ?? page);
          } else {
              // Fallback: الـ API لا يزال يرجع مصفوفة مباشرة
              setTransactions(Array.isArray(data) ? data : []);
              setTotalPages(1);
              setTotalCount(Array.isArray(data) ? data.length : 0);
              setCurrentPage(1);
          }

          setStatistics(data.statistics || null);
      } catch (err) {
          setError('فشل في تحميل حركات الخزينة.');
      } finally {
          setLoading(false);
      }
  }, [filters]);

  // جلب كل الحركات المطابقة للفلاتر دفعة واحدة — للطباعة/التصدير فقط
  const loadAllTransactionsForPrint = useCallback(async () => {
      const res = await fetchTreasuryTransactions({ ...filters, page: 1, page_size: 5000 });
      const data = res.data;
      return data.results !== undefined ? (data.results ?? []) : (Array.isArray(data) ? data : []);
  }, [filters]);

  useEffect(() => {
      loadTreasury();
      loadTransactions(1);
  },[loadTreasury, loadTransactions]);

  const handleFilterChange = useCallback((e) => {
      const { name, value } = e.target;
      setFilters(prev => ({ ...prev, [name]: value }));
      setCurrentPage(1);
  },[]);

  const goToPage = useCallback((page) => {
      if (page < 1 || page > totalPages) return;
      loadTransactions(page);
  }, [totalPages, loadTransactions]);

  return {
      treasury, transactions, statistics, loading, error, filters, handleFilterChange, loadTreasury, loadTransactions,
      currentPage, totalPages, totalCount, pageSize: PAGE_SIZE, goToPage,
      loadAllTransactionsForPrint,
  };
};